<template>
    <div :class="{'plp': true, 'drawer-open' : showFilters, }">
        <!-- BREADCRUMBS, FILTER, AND SORT-->
        <div class="filter-bar">
            <!-- BREADCRUMBS AND COUNT-->
            <div style="z-index: 1" class="filter-breadcrumbs">
                <!-- BREADCRUMBS -->
                <div class="breadcrumb-container" v-if="showBreadcrumbs">
                    <!-- MOBILE -->
                    <div class="breadcrumb desktop-hidden">
                        <ArrowGrey alt="arrow left" />
                        <Link as="span" :href="previousCategory.href" class="paragraph-caption text-rothcoText">{{ previousCategory.text }}</Link>
                    </div>
                    <!-- DESKTOP -->
                    <div class="breadcrumb mobile-hidden">
                        <Link as="span" :href="route('home')" class="paragraph-caption text-rothcoText link">Home</Link>
                    </div>
                    <div v-for="(category,index) in categoryHierarchy" :key="index" class="breadcrumb mobile-hidden">
                        <ArrowGrey class="arrow-right" alt="arrow right" />
                        <Link as="span" :href="route('category.shop-all', [category.seo_name])" class="paragraph-caption text-rothcoText link">{{ category.category_name }}</Link>
                    </div>
                </div>
                <p class="paragraph-feature filter-count">{{ searchSpringData.pagination.totalResults }} Products</p>
            </div>
            <div class="filter-sort-buttons">
                <button class="filter-button" @click="toggleFilters()">
                    <span class="desktop-hidden">Sort & Filters </span>
                    <span class="mobile-hidden" id="toggleFiltersButton">{{ showFilters ? "Hide" : "Show" }} Filters </span>
                    <span class="applied-filters">({{ activeFilters ? activeFilters.length : 0 }})</span>
                </button>
                <details class="sort-container mobile-hidden" ref="sortDropdown">
                    <summary class="sort-button" aria-label="Sort products">
                        Sort By: {{ sortOption?.label }}
                    </summary>
                    <ul class="sort-content">
                        <li v-for="(option, index) in sortOptions" :class="{'sort-select paragraph-default': true, 'selected': option.label === sortOption?.label}" :key="index">
                            <a @click.prevent="setSort(option)" href="#" :aria-label="`Sort by ${option.label}`">
                                {{ option.label }}
                            </a>
                        </li>
                    </ul>
                </details>
            </div>
        </div>
        <div class="container-filter-product-grid">
            <!-- FILTER MENU-->
            <ProductFilterDrawer
                v-model:showDrawer="showFilters"
                v-model:activeFilters="activeFilters"
                v-model:sortOption="sortOption"
                :sortOptions="sortOptions"
                :facets="searchSpringFacets"
                :filtersKey="`filters-${paginationKey}`"
                :numResults="toNumber(searchSpringData.pagination.totalResults)"
                :isDealer="is_dealer"
            />
            <div class="product-grid-container">
                <div class="product-grid">
                    <product-card
                    v-for="product in productData"
                    :key="product.product_id"
                    v-bind="cardDetails(product)"
                    :class="{
                        'fill-incomplete-row': product.fill_incomplete_row
                    }"
                    :activeColors="activeColors"
                    :productGrid="{filter_drawer_open: showFilters}"
                />
                </div>
                <pagination
                    v-if="paginationKey > 0"
                    :pagination="searchSpringPagination"
                    @pageChange="handlePagination"
                    :key="paginationKey"
                />
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import {
        ref,
        reactive,
        markRaw,
        computed,
        watch,
        toRefs,
        nextTick,
        inject,
        type PropType,
        onMounted,
    } from "vue";
    import { useDebounceFn } from "@vueuse/core";
    import Pagination from "./Pagination.vue";
    import ProductFilterDrawer from "@/components/ProductFilterDrawer.vue";
    import ProductCard from "./ProductCard.vue";
    import { useSettingsStore } from "@/store/settings";
    import { storeToRefs } from "pinia";
    // import { route } from "ziggy-js";
    import { Link } from "@inertiajs/vue3";
    import { onClickOutside } from '@vueuse/core'

    import { useFavoritesStore } from "@/store/favorites";
    import productVariantSwatches from "@/helpers/composables/productVariantSwatches.ts";
    import ArrowGrey from "@icons/arrow-grey.svg";
    import { useGenericStore } from "@/store/genericStore";
    import { useCasts } from "@/helpers/composables/casts";
    interface SearchSpringData {
        results: Array<any>;
        facets: Array<any>;
        pagination: any;
        sorting: {
            options: Array<any>;
        };
        merchandising: {
            inline: [
                {
                    value: string;
                    config: {
                        position: {
                            index: number;
                        };
                    };
                }
            ];
        };
    }

    interface Sort {
        field: string;
        direction: string;
    }

    let props = defineProps({
        searchSpringData: {
            type: Object as PropType<SearchSpringData>,
            required: true,
            default() {
                return {
                    facets: []
                };
            }
        },
        handleSort: {
            type: Function as PropType<Function>,
            required: false
        },
        handleFilter: {
            type: Function as PropType<Function>,
            required: false
        },
        page: {
            type: Number as PropType<number>,
            required: false
        },
        handlePagination: {
            type: Function as PropType<Function>,
            required: false
        },
        paginationKey: {
            type: Number,
            required: true
        },
        showBreadcrumbs: {
            type: Boolean,
            default: false
        },
        category: {
            type: Object,
            required: false,
            default() {
                return {};
            }
        },
        searchTerm: {
            type: String,
            required: false,
            default: ""
        }
    });

    let route = inject("route");
    const categoryHierarchy = computed(()=>{
        let getCategoryName = (cat)=>{
            return cat.parent ? [...getCategoryName(cat.parent),cat] : [cat];
        }
        return getCategoryName(props.category);
    })
    let { productVariants, numberOfColors } = productVariantSwatches();

    let { searchSpringData, paginationKey } = toRefs(props);

    let { handleSort, handlePagination, handleFilter } = props;

    let searchSpringFacets = computed(()=>{
        return markRaw(searchSpringData.value.facets.map((facet)=>{
            return reactive(facet);
        }));
    })

    let favoritesStore = useFavoritesStore();
    let { favorites } = storeToRefs(favoritesStore);
    let favoriteIds = computed(()=>{
        return favorites.value.map((favorite) => favorite.item_index);
    })

    let showFilters = ref(false);

    const toggleFilters = ()=>{
        showFilters.value = !showFilters.value;
    }

    const closeDrawer = ()=>{
        showFilters.value = false;
    }

    let visibleFiltersClass = computed(() => {
        return {
            "w-1/4": showFilters.value
        };
    });

    let productTilesClass = computed(() => {
        return {
            "w-3/4": showFilters.value,
            "w-full": !showFilters.value
        };
    });

    let productTilesGridClass = computed(() => {
        return {
            "grid-cols-4": !showFilters.value,
            "grid-cols-3": showFilters.value,
            grid: true,
            "gap-4": true
        };
    });

    let sortInput = ref();

    let productData = computed(() => {
        // let products: any[] = [];
        let results: any[] = searchSpringData.value.results.filter((result => result.seo_name));
        let inlineMerchandising =
            searchSpringData.value.merchandising.content?.inline ?? [];
        for (let inlineItem of inlineMerchandising) {
            let index = inlineItem.config.position.index;
            let value = inlineItem.value;
            let parser = new DOMParser();
            let doc = parser.parseFromString(value, "text/html");
            let merchandisingInfo = doc.querySelector("script");
            let bannerType = merchandisingInfo?.getAttribute("data-banner-type");
            let imageUrl = merchandisingInfo?.getAttribute("data-image-url");
            let title = merchandisingInfo?.getAttribute("data-target-title");
            let imageAlt = merchandisingInfo?.getAttribute("data-image-alt");
            let targetUrl = merchandisingInfo?.getAttribute("data-target-url");


            results.splice(index, 0, {
                type: "merchandising",
                value: {
                    bannerType,
                    imageUrl,
                    title,
                    imageAlt,
                    targetUrl
                }
            });
        }

        return results.map((data, index)=>{
            return {
                ...data,
                fill_incomplete_row: (index+1) >= (results.length - (results.length % 7) + 1) && (index+1) <= (results.length)
            }
        });
    });

    let searchSpringPagination = computed(() => {
        // convert pagination data to match the pagination component
        return {
            totalItems: searchSpringData.value.pagination.totalResults,
            currentPage: searchSpringData.value.pagination.currentPage,
            pageSize: searchSpringData.value.pagination.perPage,
            maxPages: searchSpringData.value.pagination.totalPages
        };
    });

    const bestSellers = computed(()=>{
        //if filtering by best sellers, return all products
        if(activeFilters.value.find((filter) => filter.facet?.field == 'is_best_seller')){
            return searchSpringData.value.results;
        }
        // get the top 10% of products by best_seller_rank
        let products = searchSpringData.value.results.filter((product) => product.best_seller_rank !== null && product.best_seller_rank !== undefined);
        products.sort((a, b) => a.best_seller_rank - b.best_seller_rank);
        let topTenPercentCount = Math.ceil(products.length * 0.1);
        return products.slice(0, topTenPercentCount);
    })

    const sortOption = defineModel("sortOption", {
        type: Object as PropType<Sort>
    });

    const sortDropdown = ref(null);
    onClickOutside(sortDropdown, ()=>{
        sortDropdown.value.open = false;
    })

    const sortOptions = computed(()=>{
        return (searchSpringData.value.sorting.options ?? []).filter((sortOption)=>{
            return is_dealer.value ? sortOption.field != 'price' : true;
        });
    })
    watch(sortOption, (newVal, oldVal) => {
        handleSort(newVal);
    });

    const facetsToExclude = ['is_new', 'b2b_product'];

    const initialFilters = computed(()=>{
        const arr = searchSpringData?.value?.filterSummary?.filter(activeFilter => {
            return !facetsToExclude.includes(activeFilter.field)
        }).map((activeFilter)=>{
            return {
                value: {
                    ...activeFilter,
                    low: activeFilter?.value?.rangeLow,
                    high: activeFilter?.value?.rangeHigh,
                    label: activeFilter.filterValue
                },
                facet: searchSpringData?.value.facets.find((facet)=>(facet.field == activeFilter.field)),
                active: true
            }
        })
        const categoryBreadcrumb = searchSpringData?.value?.breadcrumbs?.find((breadcrumb)=>(breadcrumb.field == 'ss_category_hierarchy'));
        if(categoryBreadcrumb){
            const facet = searchSpringData?.value?.facets?.find((facet)=>(facet.field == 'ss_category_hierarchy'));
            arr.push({
                facet: facet,
                value: {
                    label: categoryBreadcrumb.filterValue?.split(facet.hierarchyDelimiter).pop(),
                    value: categoryBreadcrumb.filterValue
                }
            })
        }
        return arr;
    })

    let activeFilters = ref([]);
    let isInitialized = ref(false);

    watch(()=>(searchSpringData), (newVal) => {
        if(activeFilters.value?.length == 0 && newVal?.value?.filterSummary){
            if(initialFilters.value?.length > 0) activeFilters.value = initialFilters.value;
            nextTick(()=>{
                isInitialized.value = true;
            })
        }
    }, {immediate: true, deep: true});

    const handleFilterDebounce = useDebounceFn((newVal) => {
        handleFilter(newVal);
    }, 200);

    watch(() => [...activeFilters.value], (newVal)=>{
        if(isInitialized.value){
            handleFilterDebounce(newVal);
        }
    });

    const { is_dealer } = storeToRefs(useSettingsStore());


    function setSort(option: any) {
        sortOption.value = option;
        sortDropdown.value.open = false;
    }

    function cardDetails(product){
        if(product.type == "merchandising"){
            return {
                type: "merchandising",
                merchandising: product.value
            }
        }
        return {
            productCardDetails: {
                title: product.name,
                price: product.price,
                seo_name: product.seo_name,
                intellisuggestData: product.intellisuggestData,
                intellisuggestSignature: product.intellisuggestSignature,
                productImages: [
                    {
                        image: product.thumbnailImageUrl
                    }
                ],
                isB2B: is_dealer,
                variants: productVariants(product.variants),
                numberOfColors: numberOfColors(productVariants(product.variants)),
                rating: product.rating,
                reviewCount: product.ratingCount,
                product_id: product.product_id,
                favorite_id: favorites.value.find(favorite=>favorite.item_index == product.product_id)?.favorite_id,
                isFavorite: favoriteIds.value.find(item => item == product.product_id) != undefined,
                bestSeller: bestSellers.value.find(item => item.product_id == product.product_id) != undefined,
            },
            allowQuickView: "true"
        }
    }


    let previousCategory = computed(()=>{
        let prev = categoryHierarchy.value[categoryHierarchy.value.length - 2];
        if(prev){
            return {href: route('category.shop-all', [prev.seo_name]), text: prev.category_name};
        }
        return {href: route('home'), text: 'Home'};
    })

    let colorSwatchesStore = useGenericStore('colorSwatches');
    let { state: colorSwatches} = storeToRefs(colorSwatchesStore);
    let activeColors = computed(()=>{
        if(props.searchTerm.length > 0){

            // if the search term is exactly a color name, return that color
            let exactMatch = colorSwatches.value?.find(color => color.name.toLowerCase() === props.searchTerm.toLowerCase());
            let colors = [];
            if(exactMatch){
                colors.push(exactMatch.name);
            }
            let keywords = props.searchTerm.split(' ');

            for(let i = 0; i < keywords.length; i++){
                let keyword = keywords[i].toLowerCase();

                // if the keyword is a color name, add it to the colors array
                let colorMatch = colorSwatches.value?.find(color => color.name.toLowerCase() === keyword);
                if(colorMatch){
                    colors.push(colorMatch.name);
                }

                // check for multi-spaced color names
                let multiSpaceMatch = colorSwatches.value?.find(color => {
                    return color.name.toLowerCase().split(' ').some(name => name.includes(keyword));
                });
                if(multiSpaceMatch){
                    colors.push(multiSpaceMatch.name);
                }

                // // if the keyword is a color family, add all colors from that family
                let familyMatch = colorSwatches.value?.filter(color => {
                    return color.colorFamily?.some(family => family.name.toLowerCase().includes(keyword)) && color.colorFamily?.length > 0;
                });

                if(familyMatch){
                    colors.push(...familyMatch.map(color => color.name));
                }
            }

            colors.push(...activeFilters.value.filter((filter) => filter.facet?.field == 'filter_color').map((filter) => filter.value.value))
            return [...new Set(colors.map(color=>(color.trim())))];
        }

        return activeFilters.value.filter((filter) => filter.facet?.field == 'filter_color').map((filter) => filter.value.value);
    })

    let { toNumber } = useCasts();
</script>
